import { useState } from 'react';
import Input from 'components/ui/Input';
import Button from 'components/ui/Button';
import { useForm } from 'react-hook-form';
import { Check, Edit3, X } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUserProfile } from '../hooks/useUserProfile';
import { profileSchema } from '../validations/userProfile';
import { useProfileUpdate } from '../hooks/useProfileUpdate';

const UserProfile = () => {
  const { data } = useUserProfile();
  const user = data?.data;
  const { mutate: updateProfile, isPending: isUpdating } = useProfileUpdate();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(profileSchema),
    values: {
      name: user?.name || '',
      email: user?.email || '',
      phoneNumber: user?.phoneNumber || '',
      twoFactorAuthEnabled: user?.twoFactorAuth?.isEnabled || false,
      twoFactorAuthSecret: user?.twoFactorAuth?.secret || '',
      ipWhitelist: user?.ipWhitelist?.join(', ') || '',
    },
  });

  // Watch two-factor auth enabled state
  const isTwoFactorEnabled = watch('twoFactorAuthEnabled');

  const onSubmit = (data) => {
    const ipWhitelist = data.ipWhitelist
      ? data.ipWhitelist
          .split(',')
          .map((ip) => ip.trim())
          .filter((ip) => ip)
      : [];

    // Prepare payload
    const payload = {
      name: data.name,
      email: data.email,
      phoneNumber: data.phoneNumber || null,
      ipWhitelist,
      twoFactorAuth: {
        isEnabled: data.twoFactorAuthEnabled,
        ...(data.twoFactorAuthEnabled &&
          data.twoFactorAuthSecret && {
            secret: data.twoFactorAuthSecret,
          }),
      },
    };

    updateProfile(payload, {
      onSuccess: () => {
        setIsEditing(false);
      },
    });
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
      <div>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-foreground'>
            Personal Information
          </h3>
          {!isEditing && (
            <Button
              type='button'
              onClick={() => setIsEditing(true)}
              className='flex items-center space-x-2'
              size='sm'
            >
              <Edit3 size={16} />
              <span className='hidden sm:inline'>Edit</span>
            </Button>
          )}
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6'>
          {/* Name */}
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>
              Full Name <span className='text-destructive'>*</span>
            </label>
            <Input
              {...register('name')}
              disabled={!isEditing}
              placeholder='Enter your full name'
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p className='mt-1 text-xs text-destructive'>
                {errors.name.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>
              Email Address <span className='text-destructive'>*</span>
            </label>
            <Input
              {...register('email')}
              type='email'
              disabled={!isEditing}
              placeholder='Enter your email'
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className='mt-1 text-xs text-destructive'>
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>
              Phone Number
            </label>
            <Input
              {...register('phoneNumber')}
              disabled={!isEditing}
              placeholder='Enter 10-digit phone number'
              className={errors.phoneNumber ? 'border-destructive' : ''}
              maxLength={10}
            />
            {errors.phoneNumber && (
              <p className='mt-1 text-xs text-destructive'>
                {errors.phoneNumber.message}
              </p>
            )}
          </div>

          {/* Role - Read Only */}
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>
              Role
            </label>
            <Input
              value={user?.role?.name || 'user'}
              disabled
              className='bg-muted capitalize'
            />
            <p className='text-xs text-muted-foreground mt-1'>
              Contact your administrator to change your role
            </p>
          </div>
        </div>
      </div>

      {/* Security Settings Section */}
      {/* <div className='pt-6 border-t border-border'>
        <h3 className='text-lg font-semibold text-foreground mb-4'>Security Settings</h3>

        <div className='space-y-6'>
          <div className='space-y-3'>
            <label className='flex items-start space-x-3 cursor-pointer'>
              <input
                type='checkbox'
                {...register('twoFactorAuthEnabled')}
                disabled={!isEditing}
                className='mt-1 w-4 h-4 text-primary border-border rounded focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed'
              />
              <div className='flex-1'>
                <span className='block text-sm font-medium text-foreground'>Enable Two-Factor Authentication</span>
                <p className='text-xs text-muted-foreground mt-1'>
                  Add an extra layer of security to your account with 2FA
                </p>
              </div>
            </label>

            {isTwoFactorEnabled && (
              <div className='ml-7 mt-3'>
                <label className='block text-sm font-medium text-foreground mb-2'>Two-Factor Secret Key</label>
                <Input
                  {...register('twoFactorAuthSecret')}
                  disabled={!isEditing}
                  placeholder='Enter your 2FA secret key'
                  className={errors.twoFactorAuthSecret ? 'border-destructive' : ''}
                />
                {errors.twoFactorAuthSecret && (
                  <p className='mt-1 text-xs text-destructive'>{errors.twoFactorAuthSecret.message}</p>
                )}
                <p className='text-xs text-muted-foreground mt-1'>
                  This secret key is used to generate time-based one-time passwords (TOTP)
                </p>
              </div>
            )}
          </div>

     
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>IP Whitelist</label>
            <Input
              {...register('ipWhitelist')}
              disabled={!isEditing}
              placeholder='Enter IP addresses separated by commas (e.g., 192.168.1.1, 14.195.82.146)'
              className={errors.ipWhitelist ? 'border-destructive' : ''}
            />
            {errors.ipWhitelist && <p className='mt-1 text-xs text-destructive'>{errors.ipWhitelist.message}</p>}
            <div className='mt-2'>
              <p className='text-xs text-muted-foreground'>
                Current whitelist:{' '}
                {user?.ipWhitelist?.length > 0 ? (
                  <span className='font-medium text-foreground'>{user.ipWhitelist.join(', ')}</span>
                ) : (
                  <span className='text-muted-foreground'>None</span>
                )}
              </p>
              <p className='text-xs text-muted-foreground mt-1'>
                Only whitelisted IP addresses will be able to access your account
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Action Buttons */}
      {isEditing && (
        <div className='flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-border'>
          <Button
            type='submit'
            disabled={!isDirty || isUpdating}
            className='flex items-center justify-center space-x-2'
          >
            {isUpdating ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Check size={16} />
                <span>Save Changes</span>
              </>
            )}
          </Button>
          <Button
            type='button'
            variant='outline'
            onClick={handleCancel}
            disabled={isUpdating}
            className='flex items-center justify-center space-x-2'
          >
            <X size={16} />
            <span>Cancel</span>
          </Button>
        </div>
      )}

      {/* Additional Account Info - Mobile */}
      <div className='md:hidden pt-6 border-t border-border space-y-4'>
        <h3 className='text-lg font-semibold text-foreground'>
          Account Information
        </h3>
        <div className='space-y-3'>
          <div>
            <p className='text-xs text-muted-foreground mb-1'>Phone Number</p>
            <p className='text-sm font-medium text-foreground'>
              {user?.phoneNumber || 'Not set'}
            </p>
          </div>
          <div>
            <p className='text-xs text-muted-foreground mb-1'>Member Since</p>
            <p className='text-sm font-medium text-foreground'>
              {user?.createdAt
                ? new Date(user.createdAt).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
          <div>
            <p className='text-xs text-muted-foreground mb-1'>
              Two-Factor Auth
            </p>
            <p className='text-sm font-medium text-foreground'>
              {user?.twoFactorAuth?.isEnabled ? (
                <span className='text-green-600'>Enabled</span>
              ) : (
                <span className='text-muted-foreground'>Disabled</span>
              )}
            </p>
          </div>
          <div>
            <p className='text-xs text-muted-foreground mb-1'>IP Whitelist</p>
            <p className='text-sm font-medium text-foreground'>
              {user?.ipWhitelist?.length > 0
                ? `${user.ipWhitelist.length} IP(s)`
                : 'None'}
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default UserProfile;
